import { useEffect, useCallback } from 'react';
import { X, Trash2 } from 'lucide-react';
import { useSimulationStore, useGraph } from '../../../store/simulationStore';
import { DistributionInspector } from './DistributionInspector';
import { FormulaField } from './FormulaField';
import { UnitAutocomplete } from '../../../components/UnitAutocomplete';
import { NoDrag } from './NoDrag';
import { TagInput } from './TagInput';

interface NodeOptionsPanelProps {
  nodeId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const NodeOptionsPanel = ({ nodeId, isOpen, onClose }: NodeOptionsPanelProps) => {
  const graph = useGraph();
  const updateNode = useSimulationStore((state) => state.updateNode);
  const removeNode = useSimulationStore((state) => state.removeNode);

  const node = nodeId ? graph.nodes.find((n) => n.id === nodeId) : null;

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  // Prevent clicks inside panel from closing it
  const handlePanelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleDelete = useCallback(() => {
    if (nodeId) {
      removeNode(nodeId);
      onClose();
    }
  }, [nodeId, removeNode, onClose]);

  if (!isOpen || !node || !nodeId) {
    return null;
  }

  const { data } = node;

  return (
    <div className="node-options-panel-backdrop" onClick={handleBackdropClick}>
      <div className="node-options-panel" onClick={handlePanelClick}>
        {/* Header */}
        <div className="node-options-panel-header">
          <h2 className="node-options-panel-title">Node Settings</h2>
          <div className="node-options-panel-header-actions">
            <button
              className="node-options-panel-delete"
              onClick={handleDelete}
              aria-label="Delete node"
              title="Delete this node"
            >
              <Trash2 size={18} />
            </button>
            <button
              className="node-options-panel-close"
              onClick={onClose}
              aria-label="Close"
              title="Close (Esc)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="node-options-panel-content">
          {/* Node Information Section */}
          <section className="node-options-section">
            <h3 className="node-options-section-title">Node Information</h3>
            
            <div className="node-options-field">
              <label className="node-options-label">
                Name
              </label>
              <input
                type="text"
                className="node-options-input"
                value={data.name}
                onChange={(e) => {
                  updateNode(nodeId, (node) => {
                    node.data.name = e.target.value;
                  });
                }}
                placeholder="Node name"
              />
            </div>

            <div className="node-options-field">
              <label className="node-options-label">
                Description
              </label>
              <textarea
                className="node-options-textarea"
                value={data.description || ''}
                onChange={(e) => {
                  updateNode(nodeId, (node) => {
                    node.data.description = e.target.value || undefined;
                  });
                }}
                placeholder="Optional description"
                rows={3}
              />
            </div>

            <div className="node-options-field">
              <label className="node-options-label">
                Tags
              </label>
              <TagInput
                value={data.tags || []}
                onChange={(tags) => {
                  updateNode(nodeId, (node) => {
                    node.data.tags = tags.length > 0 ? tags : undefined;
                  });
                }}
                placeholder="Add tags (e.g., revenue, cost, variable-cost)"
              />
            </div>
          </section>

          {/* Distribution Settings */}
          {data.type === 'distribution' && (
            <section className="node-options-section">
              <h3 className="node-options-section-title">Distribution Settings</h3>
              
              <div className="node-options-field">
                <label className="node-options-label">Unit</label>
                <UnitAutocomplete
                  value={data.unit ?? ''}
                  onChange={(value) =>
                    updateNode(nodeId, (node) => {
                      if (node.data.type === 'distribution') {
                        node.data.unit = value || undefined;
                      }
                    })
                  }
                  className="node-options-unit-input"
                />
              </div>

              <DistributionInspector nodeId={nodeId} node={data} />
            </section>
          )}

          {/* Formula Settings */}
          {data.type === 'formula' && (
            <section className="node-options-section">
              <h3 className="node-options-section-title">Formula Settings</h3>
              
              <FormulaField
                nodeId={nodeId}
                expression={data.expression || ''}
                onExpressionChange={(expression) =>
                  updateNode(nodeId, (node) => {
                    if (node.data.type === 'formula') {
                      node.data.expression = expression;
                    }
                  })
                }
              />
            </section>
          )}

          {/* Output Settings */}
          {(data.type === 'distribution' || data.type === 'formula') && (
            <section className="node-options-section">
              <h3 className="node-options-section-title">Output Settings</h3>
              
              <div className="node-options-field">
                <label className="node-options-checkbox-label">
                  <input
                    type="checkbox"
                    checked={data.isOutput ?? false}
                    onChange={(e) =>
                      updateNode(nodeId, (node) => {
                        node.data.isOutput = e.target.checked;
                        if (e.target.checked && !node.data.statistic) {
                          node.data.statistic = 'mean';
                        }
                      })
                    }
                  />
                  <span>Mark as Output</span>
                </label>
              </div>

              {data.isOutput && (
                <div className="node-options-field">
                  <label className="node-options-label">Statistic</label>
                  <select
                    className="node-options-select"
                    value={data.statistic ?? 'mean'}
                    onChange={(e) =>
                      updateNode(nodeId, (node) => {
                        node.data.statistic = e.target.value as 'mean' | 'median' | 'p10' | 'p90' | 'std' | 'value';
                      })
                    }
                  >
                    <option value="mean">Mean</option>
                    <option value="median">Median</option>
                    <option value="p10">P10</option>
                    <option value="p90">P90</option>
                    <option value="std">Std Dev</option>
                    <option value="value">Raw Samples</option>
                  </select>
                </div>
              )}
            </section>
          )}

          {/* Actions Section */}
          <section className="node-options-section node-options-actions">
            <button
              className="node-options-delete-button"
              onClick={handleDelete}
              title="Delete this node"
            >
              Delete Node
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

